class TransactionUser {
  first_name!: string;
  last_name!: string;
  email!: string;
  uuid!: string;

  constructor(data: any = {}) {
    this.build(data);
  }

  private build(data: any = {}) {
    this.first_name = data.first_name;
    this.last_name = data.last_name;
    this.uuid = data.uuid;
    this.email = data.email;
  }
}

class TransactionBeneficiary {
  name!: string;
  address!: string;
  iban!: string;
  bic!: string;
  note!: string;
  revolupay!: string;
  revolupay_id!: string;

  constructor(data: any = {}) {
    this.build(data);
  }

  private build(data: any = {}) {
    this.name = data.name;
    this.address = data.address;
    this.iban = data.iban;
    this.bic = data.bic;
    this.note = data.note;
    this.revolupay = data.revolupay;
    this.revolupay_id = data.revolupay_id;
  }
}

export class TransactionSender {
  name!: string;
  address!: string;
  crypto_address!: string;
  iban!: string;
  bic!: string;
  routing_number!: string;
  account_number!: string;
  bank_name!: string;

  constructor(data: any = {}) {
    this.build(data);
  }

  private build(data: any = {}) {
    this.name =
      data.name ||
      data.full_name ||
      data.sender_name ||
      data.sender_full_name ||
      '';
    this.address = data.address || data.sender_address || '';
    this.crypto_address =
      data.crypto_address ||
      data.wallet_address ||
      data.sender_wallet_address ||
      data.from_address ||
      '';
    this.iban = data.iban || data.sender_iban || '';
    this.bic = data.bic || data.sender_bic || '';
    this.routing_number =
      data.routing_number || data.sender_routing_number || '';
    this.account_number =
      data.account_number || data.sender_account_number || '';
    this.bank_name = data.bank_name || data.sender_bank_name || '';
  }
}

export interface TransactionEntry {
  accounting_type: "DEBIT" | "CREDIT";
  amount: number;
  currency: string;
}

export class Transaction {
  uuid!: string;
  noah_id!: string;
  payment_method!: string;
  service_type!: string;
  status!: string;
  transaction_type!: string;
  accounting_type!: string;
  amount!: string;
  fees!: string;
  amount_currency!: string;
  fees_currency!: string;
  entries!: TransactionEntry[];
  created_at!: string;
  refunded_at!: string;
  returned_at!: string;
  equivalent_in_euros!: string;
  deposit_id!: string;
  user!: TransactionUser;
  beneficiary!: TransactionBeneficiary;
  sender!: TransactionSender;
  tx_hash!: string;
  rfi_received_at?: string | null;
  rfi_completed_at?: string | null;
  rfi_failed_at?: string | null;

  constructor(data: any = {}) {
    this.build(data);
  }

  private build(data: any = {}) {
    this.uuid = data.uuid;
    this.noah_id = data.noah_id;
    this.payment_method = data.payment_method;
    this.service_type = data.service_type;
    this.status = data.status;
    this.transaction_type = data.transaction_type;
    this.accounting_type = data.accounting_type;
    this.created_at = data.created_at;
    this.refunded_at = data.refunded_at;
    this.amount = data.amount;
    this.fees = data.fees;
    this.amount_currency = data.amount_currency;
    this.fees_currency = data.fees_currency;
    this.entries = data.entries ?? [];
    this.user = data.user;
    this.beneficiary = new TransactionBeneficiary(data.beneficiary);
    const isCrypto =
      data.transaction_type === 'CRYPTO_PAYMENT' ||
      data.transaction_type === 'CRYPTO_DEPOSIT' ||
      data.payment_method === 'CRYPTO';

    this.sender = new TransactionSender(
      data.sender || {
        name:
          data.beneficiary?.name ||
          data.sender_name ||
          data.sender_full_name,
        address: !isCrypto ? (data.beneficiary?.address || data.sender_address) : '',
        crypto_address:
          data.beneficiary?.crypto_address ||
          data.beneficiary?.wallet_address ||
          data.sender_wallet_address ||
          data.from_address ||
          data.crypto_address ||
          (isCrypto ? data.beneficiary?.address : ''),
        iban: data.beneficiary?.iban || data.sender_iban,
        bic: data.beneficiary?.bic || data.sender_bic,
        routing_number:
          data.beneficiary?.routing_number || data.sender_routing_number,
        account_number:
          data.beneficiary?.account_number || data.sender_account_number,
        bank_name:
          data.beneficiary?.bank_name || data.sender_bank_name,
      },
    );
    this.returned_at = data.returned_at;
    this.equivalent_in_euros = data.equivalent_in_euros;
    this.deposit_id = data.deposit_id;
    this.rfi_received_at = data.rfi_received_at;
    this.rfi_completed_at = data.rfi_completed_at;
    this.rfi_failed_at = data.rfi_failed_at;
  }

  get isWaitingForRfi(): boolean {
    return Boolean(this.rfi_received_at) && !this.rfi_completed_at && !this.rfi_failed_at;
  }

  static isWaitingForRfi(transaction?: Partial<Transaction> | null): boolean {
    return Boolean(transaction?.rfi_received_at) && !transaction?.rfi_completed_at && !transaction?.rfi_failed_at;
  }
}
